import { computed, signal } from '@angular/core';
import { Subscription } from 'rxjs';

import { GlobalContactUI } from '../../domain/models/messaging/chat-ui.model';
import { formatLastSeen } from '../mappers/messaging/time-format.util';
import { ChatStateService } from './chat-state.service';

const PURPOSE_DIRECTORY_PAGE_SIZE = 50;
const PURPOSE_DIRECTORY_SEARCH_DEBOUNCE_MS = 300;

type PurposeDirectoryPhase = 'idle' | 'loading' | 'ready' | 'error';

interface PurposeDirectoryState {
  purpose: string | null;
  phase: PurposeDirectoryPhase;
  query: string;
  contacts: GlobalContactUI[];
  nextPage: number;
  totalPages: number | null;
  generation: number;
  error: unknown | null;
}

/** Reusable bounded controller for short-lived People-backed chat workflows. */
export class ChatPurposeDirectoryController {
  private readonly state = signal<PurposeDirectoryState>(emptyPurposeState(0));
  private readonly selectedUserIdsSignal = signal<ReadonlySet<number>>(new Set());
  private readonly selectedPresentations = new Map<number, GlobalContactUI>();
  private readonly selectedPresentationsRevision = signal(0);
  private excludedUserIds = new Set<number>();
  private searchTimer: ReturnType<typeof setTimeout> | null = null;
  private request: Subscription | null = null;

  readonly purpose = computed(() => this.state().purpose);
  readonly query = computed(() => this.state().query);
  readonly loading = computed(() => this.state().phase === 'loading');
  readonly error = computed(() => this.state().error);
  readonly hasMore = computed(() => {
    const current = this.state();
    if (current.purpose === null || current.phase === 'loading') return false;
    return current.totalPages === null || current.nextPage < current.totalPages;
  });
  readonly selectedUserIds = this.selectedUserIdsSignal.asReadonly();
  readonly contacts = computed(() => this.state().contacts.map((contact) => this.withLivePresence(contact)));
  readonly selectedContacts = computed(() => {
    this.selectedPresentationsRevision();
    return [...this.selectedPresentations.values()].map((contact) => this.withLivePresence(contact));
  });

  constructor(
    private readonly chatState: ChatStateService,
    private readonly presenceOwner: string,
  ) {}

  open(purpose: string, excludedUserIds: readonly number[] = []): void {
    this.cancelWork();
    const generation = this.state().generation + 1;
    this.excludedUserIds = new Set(excludedUserIds.filter(isPositiveUserId));
    this.selectedPresentations.clear();
    this.selectedPresentationsRevision.update((revision) => revision + 1);
    this.selectedUserIdsSignal.set(new Set());
    this.state.set({ ...emptyPurposeState(generation), purpose });
    this.syncPresenceClaim();
    this.requestPage('', 0, generation);
  }

  search(value: string): void {
    const current = this.state();
    if (current.purpose === null) return;
    const query = value.trim();
    this.cancelWork();
    const generation = current.generation + 1;
    this.state.set({
      purpose: current.purpose,
      phase: 'idle',
      query,
      contacts: [],
      nextPage: 0,
      totalPages: null,
      generation,
      error: null,
    });
    this.syncPresenceClaim();
    this.searchTimer = setTimeout(() => {
      this.searchTimer = null;
      this.requestPage(query, 0, generation);
    }, PURPOSE_DIRECTORY_SEARCH_DEBOUNCE_MS);
  }

  loadNext(): void {
    const current = this.state();
    if (current.purpose === null || current.phase === 'idle' || current.phase === 'loading') return;
    if (current.totalPages !== null && current.nextPage >= current.totalPages) return;
    this.requestPage(current.query, current.nextPage, current.generation);
  }

  retry(): void {
    if (this.state().phase !== 'error') return;
    this.loadNext();
  }

  setSelectedUserIds(candidateUserIds: readonly number[]): void {
    const next = new Set(candidateUserIds.filter(isPositiveUserId));
    const visibleById = new Map(
      this.state().contacts.flatMap((contact) => isPositiveUserId(contact.userId) ? [[contact.userId, contact] as const] : []),
    );
    for (const userId of [...this.selectedPresentations.keys()]) {
      if (!next.has(userId)) this.selectedPresentations.delete(userId);
    }
    for (const userId of next) {
      const presentation = visibleById.get(userId);
      if (presentation) this.selectedPresentations.set(userId, presentation);
    }
    this.selectedPresentationsRevision.update((revision) => revision + 1);
    this.selectedUserIdsSignal.set(next);
    this.syncPresenceClaim();
  }

  close(): void {
    this.cancelWork();
    const generation = this.state().generation + 1;
    this.state.set(emptyPurposeState(generation));
    this.excludedUserIds.clear();
    this.selectedPresentations.clear();
    this.selectedPresentationsRevision.update((revision) => revision + 1);
    this.selectedUserIdsSignal.set(new Set());
    this.chatState.setPresenceRelevanceClaim(this.presenceOwner, []);
  }

  destroy(): void {
    this.close();
  }

  private requestPage(query: string, page: number, generation: number): void {
    const current = this.state();
    if (current.generation !== generation || current.query !== query || current.nextPage !== page || current.purpose === null) return;
    this.state.set({ ...current, phase: 'loading', error: null });
    this.request = this.chatState.getContactsPage(query, page, PURPOSE_DIRECTORY_PAGE_SIZE).subscribe({
      next: (response) => {
        const active = this.state();
        if (active.generation !== generation || active.query !== query || active.nextPage !== page || active.purpose === null) return;
        const mapped = this.chatState.preparePurposeDirectoryContacts(
          response?.content ?? [],
          page === 0 ? [] : active.contacts,
        )
          .filter((contact) => !isPositiveUserId(contact.userId) || !this.excludedUserIds.has(contact.userId));
        const contacts = appendPurposeContacts(page === 0 ? [] : active.contacts, mapped);
        for (const contact of mapped) {
          if (isPositiveUserId(contact.userId) && this.selectedUserIdsSignal().has(contact.userId)) {
            this.selectedPresentations.set(contact.userId, contact);
          }
        }
        this.selectedPresentationsRevision.update((revision) => revision + 1);
        this.state.set({
          purpose: active.purpose,
          phase: 'ready',
          query,
          contacts,
          nextPage: page + 1,
          totalPages: nonNegativeInteger(response?.page?.totalPages),
          generation,
          error: null,
        });
        this.syncPresenceClaim();
      },
      error: (error) => {
        const active = this.state();
        if (active.generation !== generation || active.query !== query || active.nextPage !== page || active.purpose === null) return;
        this.state.set({ ...active, phase: 'error', error });
      },
    });
  }

  private withLivePresence(contact: GlobalContactUI): GlobalContactUI {
    contact = this.chatState.reconcileContactIdentity(contact);
    if (!isPositiveUserId(contact.userId)) return contact;
    const presence = this.chatState.getPresence(contact.userId);
    if (!presence) return contact;
    return {
      ...contact,
      isOnline: presence.isOnline,
      lastSeen: presence.isOnline ? undefined : formatLastSeen(presence.lastSeen),
    };
  }

  private syncPresenceClaim(): void {
    const userIds = new Set<number>(this.selectedUserIdsSignal());
    for (const contact of this.state().contacts) {
      if (isPositiveUserId(contact.userId)) userIds.add(contact.userId);
    }
    this.chatState.setPresenceRelevanceClaim(this.presenceOwner, [...userIds]);
  }

  private cancelWork(): void {
    if (this.searchTimer !== null) clearTimeout(this.searchTimer);
    this.searchTimer = null;
    this.request?.unsubscribe();
    this.request = null;
  }
}

function emptyPurposeState(generation: number): PurposeDirectoryState {
  return {
    purpose: null,
    phase: 'idle',
    query: '',
    contacts: [],
    nextPage: 0,
    totalPages: null,
    generation,
    error: null,
  };
}

function appendPurposeContacts(existing: readonly GlobalContactUI[], incoming: readonly GlobalContactUI[]): GlobalContactUI[] {
  const result = existing.slice();
  const seen = new Set(existing.map(contactIdentity));
  for (const contact of incoming) {
    const identity = contactIdentity(contact);
    if (seen.has(identity)) continue;
    seen.add(identity);
    result.push(contact);
  }
  return result;
}

function contactIdentity(contact: GlobalContactUI): string {
  return isPositiveUserId(contact.userId) ? `user:${contact.userId}` : `employee:${contact.employeeId}`;
}

function isPositiveUserId(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value > 0;
}

function nonNegativeInteger(value: unknown): number | null {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : null;
}
